import { refreshTokenHandler, setLocaleHeader } from '@brightdrop/auth-client';
import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  type FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

import {
  SIDEKICK_ACTING_ORGANIZATION_OVERRIDE_HEADER_NAME,
  setActingOrganizationHeader,
} from './actingOrganization';

type SidekickClientErrorHandler = (
  args: string | FetchArgs,
  error: FetchBaseQueryError
) => void;

const SIDEKICK_PATH = '/sidekick';
const MISSING_API_BASE_URL_ERROR =
  'Sidekick client has no API base URL; configureSidekickClient needs a non-empty platformApiBaseUrl';

let sidekickApiBaseUrl = '';
let onError: SidekickClientErrorHandler | undefined;

const removeTrailingSlashes = (value: string): string => {
  let endIndex = value.length;

  while (endIndex > 0 && value[endIndex - 1] === '/') {
    endIndex -= 1;
  }

  return value.slice(0, endIndex);
};

const withUrl = (args: string | FetchArgs, url: string): string | FetchArgs =>
  typeof args === 'string' ? url : { ...args, url };

/**
 * Resolves the Sidekick service URL from a root platform API URL.
 *
 * @param platformApiBaseUrl - Root platform API URL without `/sidekick`.
 * @returns The normalized Sidekick service URL, or an empty string.
 */
export const getSidekickApiBaseUrl = (platformApiBaseUrl: string): string => {
  const normalizedPlatformApiBaseUrl = removeTrailingSlashes(
    platformApiBaseUrl.trim()
  );

  return normalizedPlatformApiBaseUrl === ''
    ? ''
    : `${normalizedPlatformApiBaseUrl}${SIDEKICK_PATH}`;
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '',
  prepareHeaders: async (headers) => {
    const tokenResponse = await refreshTokenHandler();

    if (tokenResponse?.accessToken) {
      headers.set('Authorization', `Bearer ${tokenResponse.accessToken}`);
    }
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json, text/plain, */*');
    }
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    const actingOrganizationOverride = headers.get(
      SIDEKICK_ACTING_ORGANIZATION_OVERRIDE_HEADER_NAME
    );
    headers.delete(SIDEKICK_ACTING_ORGANIZATION_OVERRIDE_HEADER_NAME);
    if (actingOrganizationOverride === null) {
      setActingOrganizationHeader(headers);
    } else {
      setActingOrganizationHeader(headers, actingOrganizationOverride);
    }
    setLocaleHeader(headers);

    return headers;
  },
});

const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const urlEnd = typeof args === 'string' ? args : args.url;
  const telemetryArgs = withUrl(args, `${SIDEKICK_PATH}${urlEnd}`);

  if (sidekickApiBaseUrl === '') {
    const error: FetchBaseQueryError = {
      status: 'CUSTOM_ERROR',
      error: MISSING_API_BASE_URL_ERROR,
    };
    onError?.(telemetryArgs, error);
    return { error };
  }

  const adjustedArgs = withUrl(args, `${sidekickApiBaseUrl}${urlEnd}`);
  const result = await rawBaseQuery(adjustedArgs, api, extraOptions);

  if (result.error) {
    onError?.(telemetryArgs, result.error);
  }

  return result;
};

/**
 * Configures the root platform API URL used by the Sidekick RTK client.
 *
 * @param config - Host API configuration.
 * @param config.platformApiBaseUrl - Root platform API URL without `/sidekick`;
 * unlike `SidekickApiProvider`'s `apiBaseUrl`, this client appends that segment.
 * @param config.onError - Optional host callback for API error telemetry.
 */
export const configureSidekickClient = ({
  platformApiBaseUrl,
  onError: hostOnError,
}: {
  platformApiBaseUrl: string;
  onError?: SidekickClientErrorHandler;
}): void => {
  sidekickApiBaseUrl = getSidekickApiBaseUrl(platformApiBaseUrl);
  onError = hostOnError;
};

export const initialApi = createApi({
  reducerPath: 'sidekickRtkClient',
  refetchOnMountOrArgChange: 10,
  baseQuery: dynamicBaseQuery,
  endpoints: () => ({}),
});
